import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Minus, Trash2, ArrowUpDown, Loader2, Radio } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { useHasPermission } from '@/hooks/useSession';
import { toast } from 'sonner';
import type { QuoteSensor } from '../../types';
import type { QuoteTransmitter } from '../../api';

interface QuoteSensorsTableProps {
  sensors: QuoteSensor[];
  transmitters?: QuoteTransmitter[];
  sensorsToInvoice: string[];
  onSensorsToInvoiceChange: (ids: string[]) => void;
  onSelectSensors: () => void;
  onRemoveSensor: (sensorId: string) => void;
  onRemoveAllSensors: () => void;
  onCreateInvoice?: () => void;
  /** When set, invoicing cannot run for this quote yet. Shown as the invoice
   *  button's hover reason — the click surfaces the same message. */
  createInvoiceUnavailableReason?: string;
  quoteStatus: string;
  hasCustomer?: boolean;
  isCreatingInvoice?: boolean;
  isLoading?: boolean;
}

/** Helper: access nested object values by dot-separated key for sorting */
function getNestedValue(obj: Record<string, unknown>, path: string): string {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[part];
  }
  return String(current ?? '');
}

export function QuoteSensorsTable({
  sensors,
  transmitters,
  sensorsToInvoice,
  onSensorsToInvoiceChange,
  onSelectSensors,
  onRemoveSensor,
  onRemoveAllSensors,
  onCreateInvoice,
  createInvoiceUnavailableReason,
  quoteStatus,
  hasCustomer = true,
  isCreatingInvoice,
  isLoading,
}: QuoteSensorsTableProps) {
  const { t } = useTranslation('quotes');
  const [filterQuery, setFilterQuery] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const canModifyQuotes = useHasPermission('sensors.modify.quotes');
  const canReadCustomId = useHasPermission('sensors.read.customid');
  const canReadLocation = useHasPermission('sensors.read.location');
  const canReadInvoices = useHasPermission('sensors.read.invoices');
  const canModifyInvoices = useHasPermission('sensors.modify.invoices');

  const isInvoiced = quoteStatus === 'invoiced';

  // Filter out transmitters that already have sensors linked to this quote
  const sensorStreamDevIds = useMemo(() => {
    const devIds = new Set<string>();
    for (const s of sensors) {
      if (s.stream?.name) {
        const devId = s.stream.name.split('_')[0];
        if (devId) devIds.add(devId);
      }
    }
    return devIds;
  }, [sensors]);

  const pendingTransmitters = useMemo(() => {
    if (!transmitters?.length) return [];
    return transmitters.filter((t) => !sensorStreamDevIds.has(t.devId));
  }, [transmitters, sensorStreamDevIds]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
  };

  const filteredAndSorted = useMemo(() => {
    let result = sensors;

    if (filterQuery) {
      const q = filterQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          s.customid?.toLowerCase().includes(q) ||
          s.location?.name?.toLowerCase().includes(q) ||
          s.stream?.name?.toLowerCase().includes(q)
      );
    }

    return [...result].sort((a, b) => {
      const aVal = getNestedValue(a as unknown as Record<string, unknown>, sortKey);
      const bVal = getNestedValue(b as unknown as Record<string, unknown>, sortKey);
      if (aVal < bVal) return -1 * sortDirection;
      if (aVal > bVal) return 1 * sortDirection;
      return 0;
    });
  }, [sensors, filterQuery, sortKey, sortDirection]);

  const handleAddToInvoice = (sensorId: string) => {
    onSensorsToInvoiceChange([...sensorsToInvoice, sensorId]);
  };

  const handleRemoveFromInvoice = (sensorId: string) => {
    onSensorsToInvoiceChange(sensorsToInvoice.filter((id) => id !== sensorId));
  };

  const handleAddAllToInvoice = () => {
    const newIds = new Set(sensorsToInvoice);
    filteredAndSorted.forEach((s) => newIds.add(s._id));
    onSensorsToInvoiceChange(Array.from(newIds));
  };

  const SortHeader = ({ label, field }: { label: string; field: string }) => (
    <th
      className="text-left px-2 py-2 font-medium text-xs cursor-pointer select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortKey === field && (
          <ArrowUpDown className="h-3 w-3" />
        )}
      </div>
    </th>
  );

  return (
    <div className="space-y-3">
      {/* Configured Transmitters (awaiting first data) */}
      {pendingTransmitters.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">
            {t('sensors.configuredTransmitters')} ({pendingTransmitters.length})
          </h4>
          <p className="text-xs text-muted-foreground">
            {t('sensors.transmittersDescription')}
          </p>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-2 py-2 font-medium text-xs">{t('sensors.macAddress')}</th>
                  <th className="text-left px-2 py-2 font-medium text-xs">{t('sensors.name')}</th>
                  <th className="text-left px-2 py-2 font-medium text-xs">{t('sensors.site')}</th>
                  <th className="text-left px-2 py-2 font-medium text-xs">{t('sensors.location')}</th>
                  <th className="text-left px-2 py-2 font-medium text-xs">{t('sensors.status')}</th>
                </tr>
              </thead>
              <tbody>
                {pendingTransmitters.map((tx) => (
                  <tr key={tx.id} className="border-t">
                    <td className="px-2 py-1.5 text-xs font-mono">{tx.devId}</td>
                    <td className="px-2 py-1.5 text-xs">{tx.name || '\u2014'}</td>
                    <td className="px-2 py-1.5 text-xs">{tx.site?.name || '\u2014'}</td>
                    <td className="px-2 py-1.5 text-xs">{tx.location?.name || '\u2014'}</td>
                    <td className="px-2 py-1.5 text-xs">
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        <Radio className="h-3 w-3" />
                        {t('sensors.awaitingData')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">
          {t('sensors.title')} ({sensors.length})
        </h4>
        <div className="flex gap-2">
          {canModifyQuotes && !isInvoiced && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onSelectSensors}
                disabled={!hasCustomer}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t('sensors.selectSensors')}
              </Button>
              {sensors.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={onRemoveAllSensors}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  {t('sensors.removeAll')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Invoice selection banner */}
      {sensorsToInvoice.length > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm">
          <span className="font-medium text-green-800">
            {sensorsToInvoice.length} {t('sensors.selectedForInvoice')}
          </span>
          {onCreateInvoice && (
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={onCreateInvoice}
              disabled={isCreatingInvoice}
              title={createInvoiceUnavailableReason}
            >
              {isCreatingInvoice && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('actions.createInvoice')}
            </Button>
          )}
        </div>
      )}

      {/* Filter */}
      {sensors.length > 0 && (
        <Input
          placeholder={t('sensors.filter')}
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          className="h-8 text-sm max-w-xs"
        />
      )}

      {/* Table */}
      {sensors.length === 0 && !isLoading ? (
        pendingTransmitters.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t('sensors.noSensors')}
          </p>
        )
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {canReadCustomId && <SortHeader label={t('sensors.customId')} field="customid" />}
                <SortHeader label={t('sensors.name')} field="name" />
                {canReadLocation && <SortHeader label={t('sensors.location')} field="location.name" />}
                <SortHeader label={t('sensors.stream')} field="stream.name" />
                <SortHeader label={t('sensors.calibration')} field="stream.calibration.date" />
                <SortHeader label={t('sensors.installation')} field="installationqualification.date" />
                {canReadInvoices && (
                  <th className="text-left px-2 py-2 font-medium text-xs">
                    <div className="flex items-center gap-1">
                      {t('sensors.invoices')}
                      {canModifyInvoices && !isInvoiced && sensors.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={handleAddAllToInvoice}
                          title={t('sensors.addAllToInvoice')}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </th>
                )}
                <th className="px-2 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((sensor) => {
                const isSelectedForInvoice = sensorsToInvoice.includes(sensor._id);
                const hasInvoices = sensor.invoices.length > 0;
                const cal = sensor.stream?.calibration;
                const iq = sensor.installationqualification;

                return (
                  <tr key={sensor._id} className="border-t">
                    {canReadCustomId && (
                      <td className="px-2 py-1.5 text-xs">{sensor.customid || '\u2014'}</td>
                    )}
                    <td className="px-2 py-1.5 text-xs font-medium">{sensor.name}</td>
                    {canReadLocation && (
                      <td className="px-2 py-1.5 text-xs">{sensor.location?.name || '\u2014'}</td>
                    )}
                    <td className="px-2 py-1.5 text-xs">{sensor.stream?.name || '\u2014'}</td>
                    <td className="px-2 py-1.5 text-xs">
                      {cal?.cert_number ? (
                        <div>
                          <a
                            href={`/app#/calibrationrecords?id=${cal.calibrationrecord}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {t('sensors.certNumber')}: {cal.cert_number}
                          </a>
                          {cal.date && <div className="text-muted-foreground">{t('sensors.prodDate')}: {formatDate(cal.date)}</div>}
                          {cal.due && <div className="text-muted-foreground">{t('sensors.expDate')}: {formatDate(cal.due)}</div>}
                        </div>
                      ) : (
                        '\u2014'
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                      {iq?.report_number ? (
                        <div>
                          <a
                            href={`/app#/installationqualifications?id=${iq._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {t('sensors.reportNumber')}: {iq.report_number}
                          </a>
                          {iq.date && <div className="text-muted-foreground">{t('sensors.prodDate')}: {formatDate(iq.date)}</div>}
                        </div>
                      ) : (
                        '\u2014'
                      )}
                    </td>
                    {canReadInvoices && (
                      <td className="px-2 py-1.5 text-xs">
                        <div className="flex flex-wrap items-center gap-1">
                          {sensor.invoices.map((inv) => (
                            <a
                              key={inv}
                              href={`/erp/invoices?highlight=${inv}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {inv}
                            </a>
                          ))}
                          {canModifyInvoices && !isInvoiced && (
                            <>
                              {!isSelectedForInvoice ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 text-green-600 hover:text-green-700"
                                  onClick={() => handleAddToInvoice(sensor._id)}
                                  title={t('sensors.addToInvoice')}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 text-destructive hover:text-destructive"
                                  onClick={() => handleRemoveFromInvoice(sensor._id)}
                                  title={t('sensors.removeFromInvoice')}
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-2 py-1.5">
                      {canModifyQuotes && !isInvoiced && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => {
                            if (hasInvoices) {
                              toast.warning(t('sensors.removeBlocked'));
                              return;
                            }
                            onRemoveSensor(sensor._id);
                          }}
                          title={hasInvoices ? t('sensors.removeBlocked') : t('sensors.removeFromQuote')}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
